import React, { useState } from 'react';
import styled from 'styled-components';
import { Lock, Mail, Eye, EyeOff, ShieldCheck, RefreshCw } from 'lucide-react';
import { buildApiUrl } from '../../config/api';

const LoginContainer = styled.div`
  padding: 20px 22px 28px;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
`;

// Confirmation UI buttons (single-line, consistent sizes)
const ConfirmRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;
  flex-wrap: nowrap;
`;

const ConfirmButton = styled.button`
  padding: 12px 18px;
  border-radius: 8px;
  min-height: 44px;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`;

const ConfirmGhost = styled(ConfirmButton)`
  border: 1px solid #e0e0e0;
  background: #fff;
  color: #2d3748;
`;

const ConfirmPrimary = styled(ConfirmButton)`
  border: none;
  background: #5d7231;
  color: #fff;
  &:hover { background: #4d5f28; }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  margin: 0 0 8px 0;
  color: #1a2b48;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #7a8ba0;
  font-size: 14px;
  line-height: 1.45;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 18px;
  flex: 1;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #1a2b48;
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px 12px 44px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  color: #374151;
  background: #fff;
  box-sizing: border-box;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: #5d7231;
    box-shadow: 0 0 0 3px rgba(93, 114, 49, 0.12);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const PasswordFieldInput = styled(Input)`
  padding-right: 44px;
`;

const IconWrapper = styled.div`
  position: absolute;
  left: 14px;
  color: #9ca3af;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;

  &:hover {
    color: #6b7280;
  }
`;

const OptionsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #7a8ba0;
  cursor: pointer;
`;

const Checkbox = styled.input`
  margin: 0;
  cursor: pointer;
`;

const ForgotLink = styled.button`
  background: none;
  border: none;
  color: #5d7231;
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #4d5f28;
  }
`;

const LoginButton = styled.button`
  width: 100%;
  padding: 14px 16px;
  border: none;
  border-radius: 8px;
  background: #5d7231;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 4px;
  transition: background 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);

  &:hover:not(:disabled) {
    background: #4d5f28;
    box-shadow: 0 4px 12px rgba(93, 114, 49, 0.25);
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const Divider = styled.div`
  text-align: center;
  margin: 22px 0 14px 0;
  padding-top: 18px;
  border-top: 1px solid #e2e8f0;
`;

const SignUpText = styled.p`
  margin: 0 0 12px 0;
  color: #7a8ba0;
  font-size: 14px;
`;

const SignUpLink = styled.button`
  background: none;
  border: none;
  color: #5d7231;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #4d5f28;
  }
`;

const OtpContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const OtpGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: 460px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const SecondaryButton = styled.button`
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #374151;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: #f9fafb;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

interface LoginProps {
  onSwitchToCreateAccount: () => void;
  initialEmail?: string;
  initialPassword?: string;
  /** Open the password-reset flow when this screen mounts (e.g. user already has an account). */
  startForgotPassword?: boolean;
  forgotPasswordEmailHint?: string;
  onConsumedForgotPasswordIntent?: () => void;
}

const Login: React.FC<LoginProps> = ({
  onSwitchToCreateAccount,
  initialEmail = '',
  initialPassword = '',
  startForgotPassword = false,
  forgotPasswordEmailHint = '',
  onConsumedForgotPasswordIntent,
}) => {
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState(initialPassword);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pendingOtp, setPendingOtp] = useState<{ token: string; expiresAt: string | null } | null>(null);
  const [otpCodes, setOtpCodes] = useState({ email: '', phone: '' });
  const [otpMessage, setOtpMessage] = useState<string | null>(null);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [resendingOtp, setResendingOtp] = useState(false);
  const [otpChannels, setOtpChannels] = useState<{ email?: string | null; phone?: string | null } | null>(null);
  const [requiresConfirm, setRequiresConfirm] = useState<{ message: string } | null>(null);

  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotPending, setForgotPending] = useState<{ token: string; expiresAt: string } | null>(null);
  const [forgotCode, setForgotCode] = useState('');
  const [forgotNewPass, setForgotNewPass] = useState('');
  const [forgotConfirmPass, setForgotConfirmPass] = useState('');
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotErr, setForgotErr] = useState<string | null>(null);
  const [forgotMsg, setForgotMsg] = useState<string | null>(null);
  const [forgotCodeVerified, setForgotCodeVerified] = useState(false);
  const consumedForgotIntentRef = React.useRef(false);

  React.useEffect(() => {
    if (startForgotPassword && !consumedForgotIntentRef.current) {
      consumedForgotIntentRef.current = true;
      setShowForgotPassword(true);
      if (forgotPasswordEmailHint) {
        setForgotEmail(forgotPasswordEmailHint);
      }
      onConsumedForgotPasswordIntent?.();
    }
    if (!startForgotPassword) {
      consumedForgotIntentRef.current = false;
    }
  }, [startForgotPassword, forgotPasswordEmailHint, onConsumedForgotPasswordIntent]);

  const resetForgotFlow = () => {
    setShowForgotPassword(false);
    setForgotEmail('');
    setForgotPending(null);
    setForgotCode('');
    setForgotNewPass('');
    setForgotConfirmPass('');
    setForgotErr(null);
    setForgotMsg(null);
    setForgotCodeVerified(false);
  };

  const handleForgotStart = async () => {
    setForgotErr(null);
    setForgotMsg(null);
    setForgotBusy(true);
    try {
      const response = await fetch(buildApiUrl('/api/auth/forgot-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail.trim() }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.ok === false) {
        throw new Error((data && (data.error || data.message)) || 'Could not send reset code');
      }
      setForgotPending({
        token: data.pending_token,
        expiresAt: data.expires_at || '',
      });
      setForgotMsg(data.message || 'If an account exists for this email, a reset code has been sent.');
      setForgotCodeVerified(false);
      setForgotCode('');
      setForgotNewPass('');
      setForgotConfirmPass('');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unexpected error occurred';
      setForgotErr(message);
    } finally {
      setForgotBusy(false);
    }
  };

  const handleForgotVerifyCode = async () => {
    if (!forgotPending) return;
    setForgotErr(null);
    setForgotMsg(null);
    setForgotBusy(true);
    try {
      const response = await fetch(buildApiUrl('/api/auth/verify-password-reset-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pending_token: forgotPending.token, code: forgotCode.trim() }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.ok === false) {
        throw new Error((data && (data.error || data.message)) || 'Invalid code');
      }
      setForgotCodeVerified(true);
      setForgotMsg(data.message || 'Code verified. Enter a new password below.');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unexpected error occurred';
      setForgotErr(message);
    } finally {
      setForgotBusy(false);
    }
  };

  const handleForgotResend = async () => {
    if (!forgotPending) return;
    setForgotErr(null);
    setForgotBusy(true);
    try {
      const response = await fetch(buildApiUrl('/api/auth/resend-password-reset-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pending_token: forgotPending.token }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.ok === false) {
        throw new Error((data && (data.error || data.message)) || 'Could not resend code');
      }
      setForgotMsg(data.message || 'A new code has been sent.');
      setForgotCodeVerified(false);
      setForgotCode('');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unexpected error occurred';
      setForgotErr(message);
    } finally {
      setForgotBusy(false);
    }
  };

  const handleForgotResetPassword = async () => {
    if (!forgotPending) return;
    setForgotErr(null);
    setForgotMsg(null);
    setForgotBusy(true);
    try {
      const response = await fetch(buildApiUrl('/api/auth/complete-password-reset'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pending_token: forgotPending.token,
          new_password: forgotNewPass,
          confirm_password: forgotConfirmPass,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.ok === false) {
        throw new Error((data && (data.error || data.message)) || 'Could not reset password');
      }
      setForgotMsg(data.message || 'Password updated. You can sign in now.');
      setForgotPending(null);
      setForgotCode('');
      setForgotNewPass('');
      setForgotConfirmPass('');
      setForgotCodeVerified(false);
      setShowForgotPassword(false);
      if (forgotEmail.trim()) {
        setEmail(forgotEmail.trim());
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unexpected error occurred';
      setForgotErr(message);
    } finally {
      setForgotBusy(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setPendingOtp(null);
    setOtpCodes({ email: '', phone: '' });
    setOtpMessage(null);
    setOtpError(null);
    setSubmitting(true);
    try {
      const response = await fetch(buildApiUrl('/api/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.ok === false) {
        if (response.status === 409 && data && data.requires_confirmation) {
          setRequiresConfirm({ message: data.message || 'You are already logged in elsewhere. Log out previous session(s)?' });
          return;
        }
        throw new Error((data && (data.error || data.message)) || 'Login failed');
      }

      if (data.requires_otp) {
        setPendingOtp({ token: data.pending_token, expiresAt: data.expires_at || null });
        setOtpMessage(data.message || 'Enter the verification codes we sent to finish signing in.');
        setOtpChannels(data.channels || null);
        return;
      }

      const storage = rememberMe ? window.localStorage : window.sessionStorage;
      storage.setItem('auth_token', data.token);
      storage.setItem('auth_user', JSON.stringify(data.user || {}));
      // Notify app so UI updates and panel can close
      try { window.dispatchEvent(new Event('auth:login')); } catch { }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unexpected error occurred';
      setErrorMessage(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleForceLogin = async () => {
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const response = await fetch(buildApiUrl('/api/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, force: true })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.ok === false) {
        throw new Error((data && (data.error || data.message)) || 'Login failed');
      }
      const storage = rememberMe ? window.localStorage : window.sessionStorage;
      storage.setItem('auth_token', data.token);
      storage.setItem('auth_user', JSON.stringify(data.user || {}));
      try { window.dispatchEvent(new Event('auth:login')); } catch { }
      setRequiresConfirm(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unexpected error occurred';
      setErrorMessage(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pendingOtp) return;
    setOtpError(null);
    setVerifyingOtp(true);
    try {
      const response = await fetch(buildApiUrl('/api/auth/verify-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pending_token: pendingOtp.token,
          email_code: otpCodes.email,
          phone_code: otpCodes.phone
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.ok === false) {
        throw new Error((data && (data.error || data.message)) || 'Verification failed');
      }
      const storage = rememberMe ? window.localStorage : window.sessionStorage;
      storage.setItem('auth_token', data.token);
      storage.setItem('auth_user', JSON.stringify(data.user || {}));
      try { window.dispatchEvent(new Event('auth:login')); } catch { }
      setPendingOtp(null);
      setOtpCodes({ email: '', phone: '' });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unexpected error occurred';
      setOtpError(message);
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (!pendingOtp) return;
    setOtpError(null);
    setResendingOtp(true);
    try {
      const response = await fetch(buildApiUrl('/api/auth/resend-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pending_token: pendingOtp.token })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.ok === false) {
        throw new Error((data && (data.error || data.message)) || 'Unable to resend codes');
      }
      setOtpMessage(data.message || 'A new set of verification codes has been sent.');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unexpected error occurred';
      setOtpError(message);
    } finally {
      setResendingOtp(false);
    }
  };

  const resetOtpFlow = () => {
    setPendingOtp(null);
    setOtpCodes({ email: '', phone: '' });
    setOtpMessage(null);
    setOtpError(null);
    setOtpChannels(null);
  };

  const renderOtpForm = () => (
    <form onSubmit={handleOtpSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20, flex: 1 }}>
      <OtpContainer>
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, color: '#5d7231', fontSize: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShieldCheck size={18} />
            <strong>Secure Verification</strong>
          </div>
          <p style={{ margin: '8px 0 0 0', lineHeight: 1.4 }}>
            {otpMessage || 'Enter the codes we sent to your email and phone to finish signing in.'}
          </p>
          {otpChannels && (
            <ul style={{ margin: '8px 0 0 16px' }}>
              {otpChannels.email && <li>Email sent to {otpChannels.email}</li>}
              {otpChannels.phone && <li>SMS sent to {otpChannels.phone}</li>}
            </ul>
          )}
        </div>

        <OtpGrid>
          <InputGroup>
            <Label>Email Code</Label>
            <Input
              type="text"
              placeholder="6-digit code"
              value={otpCodes.email}
              onChange={(e) => setOtpCodes(prev => ({ ...prev, email: e.target.value }))}
              required
              style={{ paddingLeft: 14 }}
            />
          </InputGroup>
          <InputGroup>
            <Label>Phone Code</Label>
            <Input
              type="text"
              placeholder="6-digit code"
              value={otpCodes.phone}
              onChange={(e) => setOtpCodes(prev => ({ ...prev, phone: e.target.value }))}
              required
              style={{ paddingLeft: 14 }}
            />
          </InputGroup>
        </OtpGrid>

        {otpError && (
          <div style={{ color: '#e53e3e', fontSize: 14 }}>{otpError}</div>
        )}

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <SecondaryButton type="button" onClick={resetOtpFlow} style={{ flexBasis: '140px' }}>
            Back to Sign In
          </SecondaryButton>
          <SecondaryButton type="button" onClick={handleResendOtp} disabled={resendingOtp} style={{ flexBasis: '160px' }}>
            <RefreshCw size={14} />
            {resendingOtp ? 'Sending…' : 'Resend Codes'}
          </SecondaryButton>
        </div>

        <LoginButton type="submit" disabled={verifyingOtp}>
          {verifyingOtp ? 'Verifying...' : 'Verify & Sign In'}
        </LoginButton>
      </OtpContainer>
    </form>
  );

  const renderForgotPassword = () => (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <ForgotLink
        type="button"
        onClick={resetForgotFlow}
        style={{ alignSelf: 'flex-start', marginBottom: 4 }}
      >
        ← Back to sign in
      </ForgotLink>

      {!forgotPending ? (
        <>
          <InputGroup>
            <Label htmlFor="forgot-email">Email</Label>
            <InputContainer>
              <IconWrapper>
                <Mail size={20} />
              </IconWrapper>
              <Input
                id="forgot-email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
              />
            </InputContainer>
          </InputGroup>
          {forgotErr && <div style={{ color: '#e53e3e', fontSize: 14 }}>{forgotErr}</div>}
          {forgotMsg && <div style={{ color: '#5d7231', fontSize: 14 }}>{forgotMsg}</div>}
          <LoginButton type="button" disabled={forgotBusy || !forgotEmail.trim()} onClick={handleForgotStart}>
            {forgotBusy ? 'Sending…' : 'Send reset code'}
          </LoginButton>
        </>
      ) : (
        <>
          <InputGroup>
            <Label htmlFor="forgot-code">Verification code</Label>
            <InputContainer>
              <IconWrapper>
                <ShieldCheck size={20} />
              </IconWrapper>
              <Input
                id="forgot-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="6-digit code"
                value={forgotCode}
                onChange={(e) => setForgotCode(e.target.value)}
                disabled={forgotCodeVerified}
              />
            </InputContainer>
          </InputGroup>

          {!forgotCodeVerified && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <SecondaryButton type="button" onClick={handleForgotVerifyCode} disabled={forgotBusy || !forgotCode.trim()}>
                {forgotBusy ? 'Checking…' : 'Verify code'}
              </SecondaryButton>
              <SecondaryButton type="button" onClick={handleForgotResend} disabled={forgotBusy}>
                Resend code
              </SecondaryButton>
            </div>
          )}

          {forgotCodeVerified && (
            <>
              <InputGroup>
                <Label htmlFor="forgot-new">New password</Label>
                <InputContainer>
                  <IconWrapper>
                    <Lock size={20} />
                  </IconWrapper>
                  <Input
                    id="forgot-new"
                    type="password"
                    autoComplete="new-password"
                    placeholder="At least 8 characters"
                    value={forgotNewPass}
                    onChange={(e) => setForgotNewPass(e.target.value)}
                  />
                </InputContainer>
              </InputGroup>
              <InputGroup>
                <Label htmlFor="forgot-confirm">Confirm new password</Label>
                <InputContainer>
                  <IconWrapper>
                    <Lock size={20} />
                  </IconWrapper>
                  <Input
                    id="forgot-confirm"
                    type="password"
                    autoComplete="new-password"
                    placeholder="Repeat new password"
                    value={forgotConfirmPass}
                    onChange={(e) => setForgotConfirmPass(e.target.value)}
                  />
                </InputContainer>
              </InputGroup>
              <LoginButton
                type="button"
                disabled={forgotBusy || !forgotNewPass || !forgotConfirmPass}
                onClick={handleForgotResetPassword}
              >
                {forgotBusy ? 'Saving…' : 'Save new password'}
              </LoginButton>
            </>
          )}

          {forgotErr && <div style={{ color: '#e53e3e', fontSize: 14 }}>{forgotErr}</div>}
          {forgotMsg && <div style={{ color: '#5d7231', fontSize: 14 }}>{forgotMsg}</div>}
        </>
      )}

      <Divider>
        <SignUpText>Need an account?</SignUpText>
        <SignUpLink type="button" onClick={() => { resetForgotFlow(); onSwitchToCreateAccount(); }}>
          Create Account
        </SignUpLink>
      </Divider>
    </Form>
  );

  const renderLoginForm = () => (
    <Form onSubmit={handleSubmit}>
      <InputGroup>
        <Label htmlFor="email">Email Address, Username, or Customer ID</Label>
        <InputContainer>
          <IconWrapper>
            <Mail size={20} />
          </IconWrapper>
          <Input
            id="email"
            type="text"
            placeholder="Enter email, username, or Customer ID"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </InputContainer>
      </InputGroup>

      <InputGroup>
        <Label htmlFor="password">Password</Label>
        <InputContainer>
          <IconWrapper>
            <Lock size={20} />
          </IconWrapper>
          <PasswordFieldInput
            id="password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <PasswordToggle
            type="button"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </PasswordToggle>
        </InputContainer>
      </InputGroup>

      <OptionsRow>
        <CheckboxLabel>
          <Checkbox
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
          />
          Remember me
        </CheckboxLabel>
        <ForgotLink type="button" onClick={() => setShowForgotPassword(true)}>
          Forgot password?
        </ForgotLink>
      </OptionsRow>

      {errorMessage && (
        <div style={{ color: '#e53e3e', fontSize: 14 }}>{errorMessage}</div>
      )}

      <LoginButton type="submit" disabled={submitting}>
        {submitting ? 'Signing In...' : 'Sign In'}
      </LoginButton>

      <Divider>
        <SignUpText>Don't have an account?</SignUpText>
        <SignUpLink type="button" onClick={onSwitchToCreateAccount}>
          Create Account
        </SignUpLink>
      </Divider>
    </Form>
  );

  return (
    <LoginContainer>
      <Header>
        <Title>{showForgotPassword ? 'Reset password' : 'Welcome Back'}</Title>
        <Subtitle>
          {showForgotPassword
            ? 'Use your email to receive a code, verify it, then set a new password.'
            : 'Sign in to access your memorial designs'}
        </Subtitle>
      </Header>

      {requiresConfirm ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ background: '#FFFBEA', border: '1px solid #FEEBC8', color: '#744210', padding: 16, borderRadius: 8, fontSize: 14 }}>
            {requiresConfirm.message}
          </div>
          <ConfirmRow>
            <ConfirmGhost type="button" onClick={() => setRequiresConfirm(null)}>Cancel</ConfirmGhost>
            <ConfirmPrimary type="button" onClick={handleForceLogin}>Log out others & Sign In</ConfirmPrimary>
          </ConfirmRow>
        </div>
      ) : pendingOtp ? renderOtpForm() : showForgotPassword ? renderForgotPassword() : renderLoginForm()}
    </LoginContainer>
  );
};

export default Login;

